/**
 * TrainingSessionAudioStatus
 *
 * Compact inline panel showing audio export status for a specific training session.
 * Displays in the session review page below the audio controls.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, Button, CircularProgress, LinearProgress, Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { formatDistanceToNow } from 'date-fns';
import trainingApi from '../../services/trainingApi';

const TrainingSessionAudioStatus = ({ sessionId }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchJobs = async () => {
    try {
      const response = await trainingApi.listAudioExports();
      const body = response?.data || response;
      const data = body?.data || body;
      const allJobs = Array.isArray(data) ? data : [];
      // Filter to only this session's jobs
      const sessionJobs = allJobs.filter(j => j.trainingSessionId === sessionId);
      setJobs(sessionJobs);
    } catch {
      // Silently fail — this is supplementary info
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (sessionId) fetchJobs(); }, [sessionId]);

  // Auto-refresh if any jobs are active
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'pending' || j.status === 'processing');
    if (hasActive) {
      const interval = setInterval(fetchJobs, 8000);
      return () => clearInterval(interval);
    }
  }, [jobs]);

  const handleDownload = async (job) => {
    try {
      const response = await trainingApi.downloadAudioExport(job._id);
      const blob = response.data;
      // If response is JSON (e.g. status update), not a file
      if (blob.type === 'application/json') return;
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const disposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      link.download = filenameMatch?.[1] || `training-session-audio-${job._id}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // ignore
    }
  };

  if (loading || jobs.length === 0) return null;

  return (
    <Box sx={{ px: 2, pb: 1 }}>
      {jobs.map(job => (
        <Box
          key={job._id}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: 1, bgcolor: job.status === 'completed' ? 'success.50' : 'grey.50',
            borderRadius: 1, border: 1, borderColor: 'divider',
          }}
        >
          {job.status === 'pending' && <HourglassEmptyIcon fontSize="small" color="action" />}
          {job.status === 'processing' && <CircularProgress size={18} />}
          {job.status === 'completed' && <CheckCircleIcon fontSize="small" color="success" />}
          {job.status === 'failed' && <ErrorIcon fontSize="small" color="error" />}

          <Typography variant="body2" sx={{ flex: 1 }}>
            Audio Export
            {job.status === 'processing' && ' — generating...'}
            {job.status === 'pending' && ' — queued'}
            {job.status === 'completed' && ' — ready'}
            {job.status === 'failed' && ' — failed'}
          </Typography>

          {job.status === 'processing' && job.progress > 0 && (
            <LinearProgress variant="determinate" value={job.progress} sx={{ width: 80 }} />
          )}

          {job.requestedAt && (
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(job.requestedAt), { addSuffix: true })}
            </Typography>
          )}

          {job.status === 'completed' && (
            <Button
              size="small" variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(job)}
            >
              Download MP3
            </Button>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default TrainingSessionAudioStatus;
